/**
 * SBAViewerPage — Main page for the SBA document viewer.
 *
 * Route: /sba/documents/:docId/viewer
 *
 * Layout: TOC sidebar (w-72) + scrolling page viewer (flex-1).
 *
 * Features:
 * - Fetches TOC + pages on mount
 * - Handles ?chunk= and ?chunks= URL params for highlight overlays
 * - Uses IntersectionObserver for lazy-loading text spans
 * - Scrolls to the relevant page when a chunk or TOC section is selected
 */
import {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import {
  fetchTOC,
  fetchPages,
  fetchBatchTextSpans,
  fetchChunksBatch,
} from '../api/viewerApi';
import type {
  TOCEntry,
  PageInfo,
  TextSpan,
  ChunkMetadata,
} from '../types/viewer';
import { usePublishChatContext } from '../shell/chatContext';
import TOCSidebar from '../components/viewer/TOCSidebar';
import DocumentSearch from '../components/viewer/DocumentSearch';
import PageRenderer from '../components/viewer/PageRenderer';
import DisplayOnlyPageFrame from '../components/viewer/DisplayOnlyPageFrame';

// ─────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────

interface ViewerState {
  tocEntries: TOCEntry[];
  pages: PageInfo[];
  displayMode: 'normal' | 'source_only';
  displayNotice: string | null;
  loading: boolean;
  error: string | null;
}

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────

export default function SBAViewerPage() {
  const { docId: docIdParam } = useParams<{ docId: string }>();
  const [searchParams] = useSearchParams();
  const docId = Number(docIdParam);

  usePublishChatContext(Number.isInteger(docId) && docId > 0 ? {
    context: { mode: 'regulatory_document', regulatory_document: { family: 'sba', document_id: docId } },
  } : null);

  // Core viewer state
  const [state, setState] = useState<ViewerState>({
    tocEntries: [],
    pages: [],
    displayMode: 'normal',
    displayNotice: null,
    loading: true,
    error: null,
  });

  // Text spans cache: page_num -> TextSpan[]
  const [textSpansCache, setTextSpansCache] = useState<
    Map<number, TextSpan[]>
  >(new Map());

  // Chunk metadata keyed by chunk_id
  const [chunkMap, setChunkMap] = useState<Map<number, ChunkMetadata>>(
    new Map(),
  );

  // Active chunk (from URL param or user click)
  const [activeChunkId, setActiveChunkId] = useState<number | null>(null);

  // Current page for TOC highlighting
  const [currentPage, setCurrentPage] = useState(1);

  // Refs
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pageRefsMap = useRef<Map<number, HTMLDivElement>>(new Map());
  const loadedSpanPages = useRef<Set<number>>(new Set());

  // ───────────────────────────────────────────────────────────────────
  // Parse URL params for chunk highlighting
  // ───────────────────────────────────────────────────────────────────

  const requestedChunkIds = useMemo((): number[] => {
    const single = searchParams.get('chunk');
    const multi = searchParams.get('chunks');
    const ids: number[] = [];
    if (single) {
      const n = Number(single);
      if (!isNaN(n)) ids.push(n);
    }
    if (multi) {
      for (const s of multi.split(',')) {
        const n = Number(s.trim());
        if (!isNaN(n)) ids.push(n);
      }
    }
    return ids;
  }, [searchParams]);

  // ───────────────────────────────────────────────────────────────────
  // Initial data fetch: TOC + Pages
  // ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isNaN(docId)) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: 'Invalid document ID',
      }));
      return;
    }

    let cancelled = false;

    setState({
      tocEntries: [],
      pages: [],
      displayMode: 'normal',
      displayNotice: null,
      loading: true,
      error: null,
    });

    async function load() {
      try {
        const [tocResp, pagesResp] = await Promise.all([
          fetchTOC(docId),
          fetchPages(docId),
        ]);

        if (cancelled) return;

        setState({
          tocEntries: tocResp.sections,
          pages: pagesResp.pages,
          displayMode: pagesResp.display_mode ?? 'normal',
          displayNotice: pagesResp.display_notice ?? null,
          loading: false,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to load document',
        }));
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [docId]);

  // ───────────────────────────────────────────────────────────────────
  // Fetch chunk metadata when URL params are present
  // ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (requestedChunkIds.length === 0 || isNaN(docId)) return;

    let cancelled = false;

    async function loadChunks() {
      try {
        const resp = await fetchChunksBatch(docId, requestedChunkIds);
        if (cancelled) return;

        const map = new Map<number, ChunkMetadata>();
        for (const chunk of resp.chunks) {
          map.set(chunk.chunk_id, chunk);
        }
        setChunkMap(map);

        // Set the first chunk as active and scroll to its page
        if (requestedChunkIds.length > 0) {
          const firstId = requestedChunkIds[0];
          if (firstId !== undefined) {
            setActiveChunkId(firstId);
            const firstChunk = map.get(firstId);
            if (firstChunk?.page_num !== null && firstChunk?.page_num !== undefined) {
              // Slight delay to let pages render first
              setTimeout(() => scrollToPage(firstChunk.page_num!), 300);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load chunk metadata:', err);
      }
    }

    loadChunks();
    return () => {
      cancelled = true;
    };
  }, [requestedChunkIds, docId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ───────────────────────────────────────────────────────────────────
  // Lazy-load text spans via IntersectionObserver
  // ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (state.pages.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Track the topmost visible page for TOC
        let topVisiblePage = currentPage;
        let topVisibleY = Infinity;

        for (const entry of entries) {
          const pageNum = Number(
            (entry.target as HTMLElement).dataset['page'],
          );
          if (isNaN(pageNum)) continue;

          if (entry.isIntersecting) {
            const rect = entry.boundingClientRect;
            if (rect.top < topVisibleY) {
              topVisibleY = rect.top;
              topVisiblePage = pageNum;
            }

            // Lazy-load text spans for this page (and neighbors)
            loadTextSpansForRange(
              Math.max(1, pageNum - 1),
              Math.min(state.pages.length, pageNum + 1),
            );
          }
        }

        if (topVisiblePage !== currentPage) {
          setCurrentPage(topVisiblePage);
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '200px 0px',
        threshold: 0.1,
      },
    );

    // Observe all page elements
    for (const [, el] of pageRefsMap.current) {
      observer.observe(el);
    }

    return () => observer.disconnect();
  }, [state.pages.length, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // ───────────────────────────────────────────────────────────────────
  // Text span loading helper
  // ───────────────────────────────────────────────────────────────────

  const loadTextSpansForRange = useCallback(
    async (fromPage: number, toPage: number) => {
      // Check if any page in range needs loading
      const needsLoad: number[] = [];
      for (let p = fromPage; p <= toPage; p++) {
        if (!loadedSpanPages.current.has(p)) {
          needsLoad.push(p);
        }
      }
      if (needsLoad.length === 0) return;

      // Mark as loading to prevent duplicate requests
      for (const p of needsLoad) {
        loadedSpanPages.current.add(p);
      }

      try {
        const resp = await fetchBatchTextSpans(docId, fromPage, toPage);
        setTextSpansCache((prev) => {
          const next = new Map(prev);
          for (const [pageStr, data] of Object.entries(resp.pages)) {
            next.set(Number(pageStr), data.text_spans);
          }
          return next;
        });
      } catch (err) {
        // Unmark failed pages so they can be retried
        for (const p of needsLoad) {
          loadedSpanPages.current.delete(p);
        }
        console.error('Failed to load text spans:', err);
      }
    },
    [docId],
  );

  // ───────────────────────────────────────────────────────────────────
  // Navigation helpers
  // ───────────────────────────────────────────────────────────────────

  const scrollToPage = useCallback((pageNum: number) => {
    const el = pageRefsMap.current.get(pageNum);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleTOCNavigate = useCallback(
    (entry: TOCEntry) => {
      if (entry.page_start !== null) scrollToPage(entry.page_start);
    },
    [scrollToPage],
  );

  const handleSearchNavigate = useCallback(
    (pageNum: number) => {
      scrollToPage(pageNum);
    },
    [scrollToPage],
  );

  const handleChunkClick = useCallback((chunk: ChunkMetadata) => {
    setActiveChunkId(chunk.chunk_id);
  }, []);

  // ───────────────────────────────────────────────────────────────────
  // Group chunks by page for efficient rendering
  // ───────────────────────────────────────────────────────────────────

  const chunksByPage = useMemo(() => {
    const map = new Map<number, ChunkMetadata[]>();
    for (const chunk of chunkMap.values()) {
      if (chunk.page_num === null) continue;
      const existing = map.get(chunk.page_num);
      if (existing) {
        existing.push(chunk);
      } else {
        map.set(chunk.page_num, [chunk]);
      }
    }
    return map;
  }, [chunkMap]);

  // ───────────────────────────────────────────────────────────────────
  // Page ref callback factory
  // ───────────────────────────────────────────────────────────────────

  const getPageRefCallback = useCallback(
    (pageNum: number) => (el: HTMLDivElement | null) => {
      if (el) {
        pageRefsMap.current.set(pageNum, el);
      } else {
        pageRefsMap.current.delete(pageNum);
      }
    },
    [],
  );

  // ───────────────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────────────

  if (state.loading) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto" />
          <p className="mt-3 text-sm text-gray-500">Loading document viewer...</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto" />
          <p className="mt-3 text-sm text-red-600">{state.error}</p>
          <Link
            to="/"
            className="mt-4 inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to search
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Top bar */}
      <header className="flex items-center gap-3 px-4 py-2 bg-white border-b border-gray-200 flex-shrink-0">
        <Link
          to="/"
          className="text-gray-500 hover:text-gray-700 transition-colors"
          title="Back to search"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-sm font-medium text-gray-800 truncate">
          SBA Document Viewer
        </h1>
        <span className="text-xs text-gray-400">
          {state.pages.length} page{state.pages.length !== 1 ? 's' : ''}
        </span>
        {requestedChunkIds.length > 0 && (
          <span className="ml-auto text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
            {chunkMap.size} chunk{chunkMap.size !== 1 ? 's' : ''} highlighted
          </span>
        )}
      </header>

      {/* Main content: sidebar + viewer */}
      <div className="flex flex-1 overflow-hidden">
        {/* TOC Sidebar */}
        <aside className="w-72 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
          <DocumentSearch
            docId={docId}
            onNavigate={handleSearchNavigate}
          />
          <div className="flex-1 overflow-hidden">
            <TOCSidebar
              entries={state.tocEntries}
              onNavigate={handleTOCNavigate}
              currentPage={currentPage}
            />
          </div>
        </aside>

        {/* Scrollable page viewer */}
        <main
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto px-4 py-4"
        >
          <div className="max-w-4xl mx-auto">
            {state.pages.map((page) => (
              <DisplayOnlyPageFrame
                key={page.page_num}
                displayMode={state.displayMode}
                displayNotice={state.displayNotice}
              >
                <PageRenderer
                  page={page}
                  pageNum={page.page_num}
                  textSpans={textSpansCache.get(page.page_num)}
                  chunks={chunksByPage.get(page.page_num) ?? []}
                  activeChunkId={activeChunkId}
                  onChunkClick={handleChunkClick}
                  observerRef={getPageRefCallback(page.page_num)}
                />
              </DisplayOnlyPageFrame>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
}
